import type { ProblemCopy } from '../../api/problem.ts';
import { unreadable } from './shared.ts';

/**
 * Everything said about a playlist the reader **owns** — the Playlists page, the editing half of the
 * playlist page, and the add-to-playlist menu on Watch, which share a directory (`playlists/`) for the
 * same reason they share this section.
 *
 * The visibility copy is the part worth reading twice, and the one place a limitation of the API earns
 * its way onto the screen: unlisted and private arrive here as the same value, so a playlist this
 * session did not write is either *definitely* public or *one of* the other two. Every sentence below is
 * written to that shape rather than around it, because the difference is whether a link the reader sent
 * somebody works.
 */
export const playlists = {
  visibility: {
    public: 'Public',
    unlisted: 'Unlisted',
    private: 'Private',
    /** Not a fourth privacy setting — the honest name for "not listed, and that is all we know". */
    notPublic: 'Not public',
    detail: {
      public: 'Listed on your channel and findable.',
      unlisted: 'Not listed, but anyone with the link can open it.',
      private: 'Only you, signed in. A link does not work for anyone else.',
    },
    unknownCaveat:
      'Unlisted and private look the same from here, so Visious can only say it is one of the two. Setting it again makes it exact.',
  },

  /** An empty title is storable, so a row with no title says so rather than showing nothing. */
  untitled: 'Untitled playlist',

  video: { one: 'video', other: 'videos' },

  title: {
    missing: 'A playlist needs a title.',
    /**
     * The cap is in **bytes**, so "76 characters" is over the limit in German and under it in English.
     * The unit stays in the sentence because it *is* the limit: a counter that said "characters" would
     * be wrong, and one with no unit would look like it counted them.
     */
    bytes: (used: number, max: number): string => `${used} of ${max} bytes`,
    bytesOver: (used: number, max: number): string => `${used} of ${max} bytes. The rest will be cut`,
    /**
     * The save Visious declines to send. `missing` above is the field-level hint under the input; this is
     * the panel for a save pressed with the box emptied, and it was an inline literal in `usePlaylist`.
     */
    missingProblem: {
      title: 'A playlist needs a title',
      detail:
        'The instance would accept an empty one, and the result is a row with nothing to click. Visious refuses it instead.',
      retryable: false,
    },
  },

  membership: {
    present: 'Already in this playlist',
    /** Saying nothing would read as "not in it", which is how a reader ends up with a duplicate. */
    unknown: 'Long playlist. Visious cannot tell whether it is already in here',
  },

  problem: {
    notFound: {
      title: 'That playlist is gone',
      detail:
        'A playlist deleted somewhere else, on the instance itself or in another tab, reads exactly this way. Reloading the list is what clears it.',
      retryable: true,
    },
    unreadable: unreadable('Could not read your playlists'),
  },

  /** §8's Playlists page itself — the list, the create form, and the card. */
  page: {
    heading: 'Playlists',
    /** One sentence — the tour of where each verb lives is cut. */
    lede: 'The playlists in your instance account.',
    loading: 'Loading your playlists…',
    listLabel: 'Your playlists',
    empty: {
      title: 'No playlists yet',
      detail:
        'There are none under this account. Creating one above is all it takes, or add a video to a new playlist from its watch page.',
    },
    create: {
      sectionLabel: 'Create a playlist',
      heading: 'New playlist',
      titleLabel: 'Title',
      titlePlaceholder: 'Road trip',
      privacyLabel: 'Who can see it',
      submit: 'Create',
      submitting: 'Creating…',
    },
    /**
     * **4e's first named victim.** An English verb in front of `Intl`'s idea of the browser's locale is
     * how this read "Updated vor 2 Wochen" on a German browser for four slices. The verb and the phrase
     * are one sentence now, and the phrase comes from the same locale the sentence does.
     */
    updated: (when: string): string => `Updated ${when}`,

    /** Manage mode: select cards, then delete or re-set who can see them, several at once. */
    manage: {
      enter: 'Manage',
      leave: 'Done',
      selectLabel: (title: string): string => `Select ${title}`,
      /** The selection's size, said once beside the way out of it — never inside the action buttons,
          which name their act and nothing else. */
      selectedCount: (count: number): string => `${count} selected`,
      clearSelection: 'Clear selection',
      deleteSelected: 'Delete',
      makePublic: 'Make public',
      makeUnlisted: 'Make unlisted',
      makePrivate: 'Make private',
      working: (done: number, total: number): string => `Working… ${done} of ${total}`,
      /** The armed Delete button's title — the Modal that used to say this is `ConfirmButton` now. */
      confirmDetail:
        'Deleting removes them from your account on the instance. There is no undo. The videos themselves are untouched, but the lists are gone.',
      failed: {
        title: 'Some changes were refused',
        detail:
          'The instance did not accept part of the change. The list below shows what it holds now, and selecting the leftovers and trying again is safe.',
        retryable: true,
      },
    },
  },

  /** The add-to-playlist disclosure on Watch — opened, never fetched on mount (see the component). */
  add: {
    open: 'Save to playlist',
    /** The grid corner button's accessible name — it says which video, like the clock above it. */
    addTo: (title: string): string => `Save ${title} to a playlist`,
    close: 'Close',
    signedOutBefore: 'Playlists live in your account on the instance, so this needs you signed in.',
    signIn: 'Sign in',
    signedOutAfter: 'and you will come straight back here.',
    loading: 'Loading your playlists…',
    /** No jump-out to the Playlists page: the create form sits right below this sentence. */
    none: 'You have no playlists yet. The first one is a title away.',
    /** The create button's promise here is bigger than "Create": it saves this video too. */
    createSubmit: 'Create & save',
    added: 'Added',
    failed: 'Failed',
  },

  /** The Watch later buttons and their picker (2026-08-02). */
  watchLater: {
    /** The pill on the watch page, both states — the hover title names the act. */
    pill: 'Watch later',
    addTitle: 'Add to Watch later',
    inPill: 'In Watch later',
    removeTitle: 'Remove from Watch later',
    /** The grid corner button's accessible names — `title` says which video, like the remove's. */
    add: (title: string): string => `Add ${title} to Watch later`,
    remove: (title: string): string => `Remove ${title} from Watch later`,
    working: 'Saving…',
    /** The picker modal. One dialog for the settings row and every button's first press. */
    pickerTitle: 'Choose your Watch later playlist',
    pickerLede:
      'Watch later saves into an ordinary playlist on your account. Pick which one, or create a fresh one below.',
    close: 'Close',
    current: 'Current',
    /** The create form's promise here: it creates the list and appoints it in one press. */
    createSubmit: 'Create & use',
    loading: 'Loading your playlists…',
    none: 'You have no playlists yet. The first one is a title away.',
    signedOutBefore: 'Watch later saves into a playlist on your account, so this needs you signed in.',
    signIn: 'Sign in',
    signedOutAfter: 'and you will come straight back here.',
  },

  /** The nouns the write failures need, which the shared taxonomy has no way to know. */
  action: {
    create: 'creating a playlist',
    update: 'saving changes',
    delete: 'deleting a playlist',
    add: 'adding a video',
    remove: 'removing a video',
  },

  write: {
    /** A malformed body is **our** bug, not the reader's, and this says so instead of offering a sign-in. */
    badBody: (action: string): ProblemCopy => ({
      title: `The instance refused this change (${action})`,
      detail:
        'Visious sent it something it would not accept. Nothing was changed, and signing in again will not help. This is a bug here, not a problem with your account.',
      retryable: false,
    }),
    playlistGone: {
      title: 'That playlist is gone',
      detail:
        'Either it was deleted somewhere else, or it is a YouTube playlist, and those cannot be changed from here.',
      retryable: false,
    },
    entryGone: {
      title: 'That entry is no longer there',
      detail:
        'It is not in the playlist any more, usually because it was already removed somewhere else. Reloading the playlist is what fixes it.',
      retryable: true,
    },
    videoGone: {
      title: 'That video cannot be added',
      detail:
        'YouTube will not serve it, so there is nothing to put in the playlist. A deleted, private or region-blocked video reads this way.',
      retryable: false,
    },
  },
} as const;
